import { useTranslation } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import React, { useRef, useMemo, forwardRef } from "react";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import img403 from "@Assets/images/noAccess.svg";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import FormFlowLibraryPopup from "./formFlowLibraryPopup";
import SlickGrid1, { withDynamicColumns } from "@CubeComponents/html/SlickGrid1";
import { formFlowServiceUrl } from "@Services/dataManager";
import _ from "lodash";
import { getUserSelection } from "../CreateInspectionAndProgress/formsHelpers";
import Text from "@CubeComponents/html/Text/Text";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProjectSettingsPageUrl } from "@Features/projects";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import useFormFlowList from "./useFormFlowList";
import { FORM_FLOWS_GRID_TYPE, getFormFlowsSelectedLayoutKey } from "@Helpers/constants";
import type { FormFlowRow } from "./formFlow.types";
import type {
  GridClickEvent,
  GridRowBase,
  LayoutData,
  SlickgridReactInstanceX,
  ToolbarItem,
} from "@CubeComponents/html/SlickGrid1";

/**
 * The grid instance this list holds a ref to, and hands to `formFlowLibraryPopup`.
 *
 * Parameterised with `GridRowBase` rather than `FormFlowRow`: the row type carries an open index
 * signature (list columns are addressed by string field name), which makes `GridRowBase`'s own
 * optional members resolve to `unknown` and so fails the `T extends GridRowBase` constraint. Rows
 * read back off the ref are still named as `FormFlowRow` in `getRowsOf`.
 */
export type FormFlowGridRef = SlickgridReactInstanceX<GridRowBase>;

/**
 * `detail` of the CustomEvent SlickGrid1 dispatches on a selection change. SlickGrid1 types the
 * callback's argument as an open `Record<string, unknown>`, so `detail` arrives as `unknown` and is
 * named here at the one place it is read.
 */
interface GridSelectionDetail {
  args: { rows: number[] };
}

/** Narrows that `detail` to the shape read below, instead of asserting the `unknown` it arrives as. */
const isGridSelectionDetail = (value: unknown): value is GridSelectionDetail => {
  if (typeof value !== "object" || value === null || !("args" in value)) return false;
  const { args } = value;
  return typeof args === "object" && args !== null && "rows" in args && Array.isArray(args.rows);
};

/**
 * A row read back off a grid.
 *
 * `slickGrid.getDataItem` is `any` on the vendor grid (`SlickGrid<TData = any>`) and hands back
 * `undefined` for a selected index that no longer maps to a row — a selection kept across a refresh
 * or a filter change. Rows are therefore taken at `unknown` and named only once they are an object;
 * every caller reads `_id` off them.
 */
const isFormFlowRow = (value: unknown): value is FormFlowRow => typeof value === "object" && value !== null;

/**
 * The creator of a row, at the type the SERVER can actually store.
 *
 * `FormFlowRow.createdBy` (formFlow.types.ts, shared with `packages/mobile`) admits only
 * `{ _id, fullName } | ObjectIdString`, but the create path stores whatever `getUserDetailsObj()`
 * returned for the session user, and that answers `null` when the id has no `accounts` row
 * (packages/server/helpers/globalFun.ts → forms.service.ts). `typeof null === "object"`, so a stored
 * `null` takes the object branch — which is why both readers below are optional-chained, and why
 * the parameter, not a local, carries the widened type: a `const` annotated with the same union
 * would be narrowed straight back to the initializer's type and the chain would go dead again.
 * (cube-qa census 2026-08-24: 270/270 rows carry an object, so this covers the writer's reachable
 * path rather than data observed there.)
 */
type StoredCreatedBy = FormFlowRow["createdBy"] | null;

/** The creator's account id, for `renderUserTag`. */
const createdById = (createdBy: StoredCreatedBy) => (typeof createdBy === "object" ? createdBy?._id : createdBy);

/** The creator's name, for sorting/filtering/export. */
const createdByName = (createdBy: StoredCreatedBy) => (typeof createdBy === "object" ? createdBy?.fullName : createdBy);

interface FormFlowsProps {
  projectId?: string;
  /** Opens a flow for editing. */
  setFormFlowId?: (formFlowId: string) => void;
  /** Selection-only mode: no toolbar, no header. */
  showOnlyGrid?: boolean;
  /** Replaces the default project query (used by the library picker). */
  customQuery?: unknown;
  /** Lets an embedding wizard enable its own Next button from this list's selection. */
  setNextBtnState?: (state: boolean) => void;
  /** Replaces the default list endpoint (used by the library picker). */
  customService?: string;
}

const FormFlows = forwardRef<FormFlowGridRef, FormFlowsProps>(
  (
    { projectId, setFormFlowId, showOnlyGrid = false, customQuery, setNextBtnState, customService },
    ref: React.ForwardedRef<FormFlowGridRef>,
  ) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [urlSearchParams] = useSearchParams();
    const formCategory = urlSearchParams.get("resourceType")?.toUpperCase();

    const tempRef = useRef<FormFlowGridRef | null>(null);
    // A `ForwardedRef` is an object ref, a callback ref, or nothing. This list needs one it can read
    // the grid instance back off, and a callback ref carries no `.current`, so only an object ref is
    // adopted; everything else falls back to the local one. Callers only ever pass an object ref
    // (`formFlowLibraryPopup` passes a `useRef`), so this is the same ref they get today.
    const dataGrid = typeof ref === "object" && ref !== null ? ref : tempRef;
    const archivedDataGrid = useRef<FormFlowGridRef | null>(null);

    /** Reads a grid's current selection as row data. */
    const getRowsOf = (gridRef: React.MutableRefObject<FormFlowGridRef | null>): FormFlowRow[] => {
      const grid = gridRef.current?.slickGrid;
      if (!grid) return [];
      return grid
        .getSelectedRows()
        .map((row: number): unknown => grid.getDataItem(row))
        .filter(isFormFlowRow);
    };

    const {
      hasAddPermission,
      listQuery,
      listService,
      archivedListQuery,
      columns,
      toolbarOptions,
      archivedToolbarOptions,
      createMenuItems,
      isLoadingComplete,
      showIllustration,
      archivedIsLoadingComplete,
      archivedShowIllustration,
      handleListLoaded,
      handleArchivedListLoaded,
      handleSelectionChange,
      handleArchivedSelectionChange,
      handleRecordClick,
      openAddEditModal,
      showDeleteConfirmationModal,
      showArchiveModal,
      addFromLibrary,
      setAddFromLibrary,
      openCreateModal,
      openLibraryModal,
      openArchiveModal,
      closeAddEditModal,
      closeDeleteModal,
      closeArchiveModal,
      handleCancelLibrary,
      handleCreateMenuSelect,
      editMode,
      formFlowData,
      formTemplates,
      setSpaceId,
      handleChange,
      isSaveDisabled,
      handleCreateFormFlowClick,
      userSelectionMode,
      handleUserSelectionModeChange,
      toolbarClickHandler,
      archivedToolbarClickHandler,
      handleDeleteFormFlow,
      refreshGrid,
      handleBackClick,
      handleViewSelected,
    } = useFormFlowList({
      projectId,
      formCategory,
      customQuery,
      serviceUrl: formFlowServiceUrl,
      customService,
      showOnlyGrid,
      setNextBtnState,
      setFormFlowId,
      getSelectedRows: () => getRowsOf(dataGrid),
      getArchivedSelectedRows: () => getRowsOf(archivedDataGrid),
      // `refresh` is optional on SlickGrid1's ref extras but is always attached by the grid; the
      // `?.` on `current` is the existing guard and the `!` is erased at emit.
      refreshList: () => dataGrid.current?.refresh!(),
      refreshArchivedList: () => archivedDataGrid.current?.refresh!(),
      onBack: () => {
        // The back arrow lives in `header()`, which only renders on the full-page list — and that is
        // mounted from `formFlowWrapper`, where `projectId` is required. The prop is optional only
        // for the selection-only embedding (`showOnlyGrid`), which renders no header at all.
        if (!projectId) return;
        navigate(getProjectSettingsPageUrl({ projectId, tab: "projectModules" }));
      },
    });

    const createTemplate = () => {
      return (
        <SelectDropDown
          items={createMenuItems}
          select={(args: { item: { id?: string } }) => handleCreateMenuSelect(args.item.id)}
          cssClass="e-caret-hide border-none ps-8 bg-white p-0"
        >
          <ButtonComponent id="add" buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
            <span className="flex gap-1 items-center justify-center">
              <Text>{t("Create")}</Text>
              <IconComponent name="arrowDownSm" width="1rem" height="1rem" />
            </span>
          </ButtonComponent>
        </SelectDropDown>
      );
    };

    /** The "Create" toolbar entry renders the split button; the rest are plain entries. */
    const gridToolbarOptions = toolbarOptions.map((option, index) =>
      index === 0 ? { ...option, template: createTemplate } : option,
    );

    const selectionEvent = (args: Record<string, unknown>) => {
      const { detail } = args;
      if (!dataGrid.current || !isGridSelectionDetail(detail)) return;
      handleSelectionChange(detail.args.rows.length);
    };

    const archivedSelectionEvent = (args: Record<string, unknown>) => {
      const { detail } = args;
      if (!archivedDataGrid.current || !isGridSelectionDetail(detail)) return;
      handleArchivedSelectionChange(detail.args.rows.length);
    };

    const recordClick = (args: GridClickEvent) => {
      const grid = dataGrid.current?.slickGrid;
      if (!grid) return;
      const rowData: unknown = grid.getDataItem(args.detail.args.row);
      if (!isFormFlowRow(rowData)) return;
      handleRecordClick({ rowData, cell: args.detail.args.cell });
    };

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      return convertDate(Number(dataContext.createdDate), "dateForTooltip");
    };

    const createdByTemplate = (
      _row: number,
      _cell: number,
      _value: FormFlowRow["createdBy"],
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      const container = document.createElement("div");
      renderUserTag(container, { id: createdById(dataContext.createdBy) });
      return container;
    };

    // Column metadata comes from the shared constants; only the cell renderers are web-specific.
    //
    // Branded with `withDynamicColumns()` rather than `typedColumns()`: `FormFlowListColumn.field`
    // is a `string` (one entry is the dotted path `space.spaceName`), so these entries cannot
    // satisfy `typedColumns<T>()`'s `field extends keyof T`. `withDynamicColumns` is the module's
    // documented constructor for exactly that mix and mints the same brand, so no assertion — and
    // no `no-unsafe-type-assertion` disable — is needed at the JSX boundary below.
    const columnDefinitions = useMemo(
      () =>
        withDynamicColumns<GridRowBase>(
          columns.map((column) => {
            switch (column.id) {
              case "selectedForm":
                return {
                  ...column,
                  formatter: (
                    _row: number,
                    _cell: number,
                    _value: FormFlowRow["selectedForm"],
                    _columnDef: unknown,
                    dataContext: FormFlowRow,
                  ) => _.get(dataContext, "selectedForm.name"),
                  _valueGetter: (row: FormFlowRow) => row.selectedForm?.name,
                };
              case "space":
                return {
                  ...column,
                  formatter: (
                    _row: number,
                    _cell: number,
                    _value: FormFlowRow["space"],
                    _columnDef: unknown,
                    dataContext: FormFlowRow,
                  ) => _.get(dataContext, "space.spaceName"),
                  _valueGetter: (row: FormFlowRow) => row.space?.spaceName,
                };
              case "createdBy":
                return {
                  ...column,
                  formatter: createdByTemplate,
                  _valueGetter: (row: FormFlowRow) => createdByName(row.createdBy),
                };
              case "createdDate":
                return { ...column, formatter: formatCreatedDate };
              default:
                // Spread like every other arm: a bare `FormFlowListColumn` is an interface and so has
                // no implicit index signature, which `withDynamicColumns`' runtime-column arm needs.
                // `getFormFlowListColumns()` already builds fresh objects per call, so the copy is
                // not observable.
                return { ...column };
            }
          }),
        ),
      [columns],
    );

    const headerContent = () => (
      <div className=" w-full flex justify-between rounded-md">
        <Text className="">{editMode ? t("Edit Form Flow") : t("Create New Form Flow")}</Text>
        {!editMode && (
          <SelectSpaces
            disablePermissionCheck
            data-testid="spaceDropdownField"
            projectId={projectId}
            selectedSpaceCallback={(params: { _id: string }) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    );

    const modelContent = () => (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text>{t("Form Flow Name")}</Text>
          <span>
            <TextFieldComponent
              name="name"
              type="text"
              placeholder={t("Add form flow name")}
              showClearButton
              onChange={handleChange}
              value={formFlowData.name}
            />
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <Text>{t("Select Template")}</Text>
          <span>
            <SelectDropDown
              searchable
              // The dialog stores the template's `_id` STRING here (`setFormFlowField` keeps
              // `value._id`, and `startEditFormFlow` seeds the row's `selectedForm._id`), but
              // `FormFlowFormValues.selectedTemplate` is `unknown`, so the id is narrowed rather
              // than asserted. `SelectDropDown` takes an id-only value (`OptionValue`) as well as a
              // whole option and matches it against `fields.value`, so this is what it already got.
              value={typeof formFlowData.selectedTemplate === "string" ? formFlowData.selectedTemplate : null}
              placeholder={t("Select Form")}
              disabled={formFlowData.formCategory == "" || editMode}
              onChange={(e) => handleChange({ target: { name: "selectedTemplate", value: e } })}
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              fields={{
                text: "name",
                value: "_id",
              }}
              options={formTemplates}
              buttonWidth="100%"
              popperWidth={300}
            />
          </span>
        </div>
        {!editMode &&
          getUserSelection(
            "Assign users to level",
            userSelectionMode,
            handleUserSelectionModeChange as (args: React.ChangeEvent<HTMLInputElement>) => void,
          )}
      </div>
    );

    const footerContent = () => (
      <div className="flex gap-4 items-center mt-5">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          disabled={isSaveDisabled}
          onClick={handleCreateFormFlowClick}
        >
          <Text>{editMode ? t("Update") : t("Create")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeAddEditModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const getGridComponent = ({
      gridRef,
      query,
      onSelectionChange,
      toolbarClick,
      toolbar,
      onRowClick,
      onDataLoaded,
      onClickLayout,
    }: {
      gridRef: React.MutableRefObject<FormFlowGridRef | null>;
      query: Record<string, unknown>;
      onSelectionChange: (args: Record<string, unknown>) => void;
      toolbarClick: (params: string) => void;
      /** Omitted in selection-only mode, where the list renders without a toolbar. */
      toolbar?: ToolbarItem[];
      onRowClick?: (args: GridClickEvent) => void;
      onDataLoaded: (count?: number) => void;
      onClickLayout?: (layoutParams: LayoutData) => void;
    }) => (
      <SlickGrid1
        ref={gridRef}
        onClickLayout={onClickLayout}
        columnDefinitions={columnDefinitions}
        enableSortingOption
        onSelectedRowIdsChanged={onSelectionChange}
        remoteDataSource={listService}
        infiniteScroll
        query={query}
        onClick={onRowClick}
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        toolbarClickHandler={toolbarClick}
        toolbar={toolbar}
        setGridReady={(_ready, count) => onDataLoaded(count)}
        enableViews={Boolean(projectId && formCategory)}
        {...(projectId && formCategory
          ? {
              viewsOptions: {
                headerName: "Views",
                name: "View",
                resourceType: formCategory,
                gridType: FORM_FLOWS_GRID_TYPE,
                selectedLayoutKey: getFormFlowsSelectedLayoutKey(formCategory),
                fetchParams: { projectId },
              },
            }
          : {})}
      />
    );

    const emptyIllustration = (show: boolean, title: string, actions?: React.ReactNode) =>
      show ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-white">
          <span className="flex flex-col items-center">
            <IconComponent name="NoData" className="h-52 w-52" />
            <Text className="text-2xl text-text_gray">{title}</Text>
          </span>
          {actions}
        </div>
      ) : null;

    const archiveModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        width="80%"
        height="90%"
        headerTitle={<Text className="text-2xl font-semibold">{t("Archived Form Flows")}</Text>}
        isVisible={showArchiveModal}
        onCancel={closeArchiveModal}
        modelContent={
          <div className="relative flex w-full h-full overflow-auto">
            <div
              className={`${archivedIsLoadingComplete && !archivedShowIllustration ? "" : "invisible"} h-full w-full overflow-auto`}
            >
              {getGridComponent({
                gridRef: archivedDataGrid,
                query: archivedListQuery,
                onSelectionChange: archivedSelectionEvent,
                toolbarClick: archivedToolbarClickHandler,
                toolbar: archivedToolbarOptions,
                onDataLoaded: handleArchivedListLoaded,
              })}
            </div>
            {emptyIllustration(archivedIsLoadingComplete && archivedShowIllustration, t("No Archived Form Flows"))}
          </div>
        }
        footerContent={
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeArchiveModal}>
            <Text>{t("Close")}</Text>
          </ButtonComponent>
        }
      />
    );

    const addEditModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={footerContent()}
        width="50%"
        headerTitle={headerContent()}
        onCancel={closeAddEditModal}
        isVisible={openAddEditModal}
        modelContent={modelContent()}
      />
    );

    const deletePopup = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={
          <div className=" flex gap-4">
            <ButtonComponent onClick={handleDeleteFormFlow} buttonType="gradientButton">
              <Text>{t("Confirm")}</Text>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeDeleteModal}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <div className=" w-full flex justify-between rounded-md">
            <Text className="">{t("Delete selected form flow(s)")}</Text>
          </div>
        }
        isVisible={showDeleteConfirmationModal}
        onCancel={closeDeleteModal}
        modelContent={
          <Text className="whitespace-normal">{t("Are you sure you want to delete the selected form flow(s) ?")}</Text>
        }
        width="30%"
      />
    );

    const noAccessPage = () => (
      <div className="flex w-full h-full overflow-auto flex-col justify-center items-center gap-1">
        <img src={img403} alt="noAccess" />
        <Text className="font-semibold text-2xl">{t("You do not have access to this page")}</Text>
      </div>
    );

    const header = () => {
      return (
        <div className="flex justify-between items-center ps-2 pt-4 pb-2 pe-4 w-full">
          <div className="flex gap-4 items-center">
            <IconComponent className="cursor-pointer" onClick={handleBackClick} name="BackwardArrow" />
            <div className="flex gap-1 flex-col">
              <Text className="text-2xl font-semibold">{t("Form Flows")}</Text>
              <Text>{t("List of all the Form Flows in this Project")}</Text>
            </div>
          </div>
          <div onClick={openArchiveModal} className="flex gap-1 cursor-pointer items-center">
            <IconComponent className="h-4 w-4" name="archive" />
            <Text>{t("View Archived")}</Text>
          </div>
        </div>
      );
    };

    const getFormFlowContent = () => (
      <div className="flex w-full flex-col h-full overflow-auto">
        {!showOnlyGrid && header()}
        <div className="relative flex w-full h-full overflow-auto">
          <div className={`${isLoadingComplete && !showIllustration ? "" : "invisible"} h-full w-full overflow-auto`}>
            {getGridComponent({
              gridRef: dataGrid,
              query: listQuery,
              onSelectionChange: selectionEvent,
              toolbarClick: toolbarClickHandler,
              toolbar: !showOnlyGrid ? gridToolbarOptions : undefined,
              onRowClick: recordClick,
              onDataLoaded: handleListLoaded,
              onClickLayout: handleViewSelected,
            })}
          </div>
          {emptyIllustration(
            isLoadingComplete && showIllustration,
            t("No Form Flow has been added yet"),
            !showOnlyGrid ? (
              <div className="flex flex-row gap-4">
                <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={openCreateModal}>
                  <span className="flex items-center">
                    <IconComponent className="h-4" name="add" />
                    <Text>{t("Create")}</Text>
                  </span>
                </ButtonComponent>
                <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={openLibraryModal}>
                  <span className="flex items-center">
                    <IconComponent className="h-4" name="add" />
                    <Text>{t("Create From Library")}</Text>
                  </span>
                </ButtonComponent>
              </div>
            ) : undefined,
          )}
        </div>
      </div>
    );

    const getFlowsFromLibrary = () => {
      return (
        <FormFlowLibraryPopup
          handleCancelibrary={handleCancelLibrary}
          projectId={projectId}
          refreshGrid={refreshGrid}
          addFromLibrary={addFromLibrary}
          setAddFromLibrary={setAddFromLibrary}
          resourceType={formFlowData.formCategory}
        />
      );
    };

    return (
      <>
        {hasAddPermission ? getFormFlowContent() : noAccessPage()}
        {openAddEditModal && addEditModal()}
        {showDeleteConfirmationModal && deletePopup()}
        {addFromLibrary.state && getFlowsFromLibrary()}
        {showArchiveModal && archiveModal()}
      </>
    );
  },
);
FormFlows.displayName = "FormFlows";
export default FormFlows;
